import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@shared/components/ui/chart';
import { cn } from '@shared/lib/utils';
import { ClassValue } from 'clsx';
import React, { ComponentType } from 'react';
import { Label, Pie, PieChart } from 'recharts';
import { PieLabel, PieSectorDataItem } from 'recharts/types/polar/Pie';
import { ActiveShape, DataKey, Margin } from 'recharts/types/util/types';
import { CustomTooltipProps } from './tooltips/types';

interface PieChartProps<T extends string, U extends string> {
  nameKey: T;
  dataKey: U;
  chartData: { [key in T | U]: key extends T ? string : number }[];
  chartConfig: ChartConfig;
  className?: ClassValue;
  innerRadius?: number;
  cornerRadius?: number;
  centerLabel?: {
    value: string | number | JSX.Element;
    label: string;
  };
  margin?: Margin;
  onClick?: (data: any, index: number, e: React.MouseEvent) => void;
  activeShape?: ActiveShape<PieSectorDataItem>;
  label?: PieLabel<any>;
  tooltipFormatter?: ComponentType<CustomTooltipProps>;
  productRatingCenter?: boolean;
}

export default function SimplePieChart<
  T extends DataKey<string>,
  U extends DataKey<string>,
>({
  nameKey,
  dataKey,
  chartConfig,
  chartData,
  centerLabel,
  className,
  innerRadius = 60,
  cornerRadius = 0,
  margin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  onClick,
  activeShape,
  label,
  tooltipFormatter: Component,
  productRatingCenter = false,
}: PieChartProps<T, U>) {
  return (
    <ChartContainer
      config={chartConfig}
      className={cn('min-h-[200px] overflow-visible p-0', className)}
    >
      <PieChart margin={margin} className="font-normal">
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              hideLabel
              className={cn('w-max', {
                'w-fit': Component,
              })}
              formatter={
                Component
                  ? (value, name, item, index) => (
                      <Component
                        value={value}
                        name={name}
                        item={item}
                        index={index}
                        chartConfig={chartConfig}
                        chartData={chartData}
                      />
                    )
                  : undefined
              }
            />
          }
        />
        <Pie
          data={chartData}
          dataKey={dataKey}
          nameKey={nameKey}
          innerRadius={innerRadius}
          strokeWidth={0}
          startAngle={90}
          endAngle={450}
          onClick={onClick}
          isAnimationActive={false}
          activeShape={activeShape}
          cornerRadius={cornerRadius}
          label={label}
        >
          <Label
            content={({ viewBox }) => {
              if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                const imageSize = 50;

                if (productRatingCenter) {
                  return (
                    <>
                      <image
                        href={`/clouds/cloud-${centerLabel?.value}.svg`}
                        x={(viewBox.cx || 0) - imageSize / 2}
                        y={(viewBox.cy || 0) - imageSize / 2}
                        width={imageSize}
                        height={imageSize}
                        preserveAspectRatio="xMidYMid slice"
                      />
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 24}
                          className="fill-muted-foreground"
                        >
                          {centerLabel?.label}
                        </tspan>
                      </text>
                    </>
                  );
                }
                return (
                  <text
                    x={viewBox.cx}
                    y={viewBox.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    <tspan
                      x={viewBox.cx}
                      y={viewBox.cy}
                      className="fill-foreground text-3xl font-bold"
                    >
                      {centerLabel?.value.toLocaleString()}
                    </tspan>
                    <tspan
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) + 24}
                      className="fill-muted-foreground"
                    >
                      {centerLabel?.label}
                    </tspan>
                  </text>
                );
              }
            }}
          />
        </Pie>
      </PieChart>
    </ChartContainer>
  );
}
